import { Injectable, Injector, NgZone } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Notification } from 'src/app/Theme/layout/admin/notification.service';
import { environment } from 'src/environments/environment';

export interface NotificationMobileToastPayload {
  title: string;
  message: string;
  createdAt?: string;
  imageUrl?: string | null;
  rawNotification: Notification;
}

/** Banner stays on screen for 3 seconds. */
export const NOTIFICATION_MOBILE_TOAST_VISIBLE_MS = 3000;

/** Must match slide-out transition in notification-mobile-toast.scss */
const MOBILE_BANNER_SLIDE_OUT_MS = 750;

const LOG_PREFIX = '[MobileTopBanner]';

@Injectable({
  providedIn: 'root'
})
export class NotificationMobileToastService {
  private readonly mobileMediaQuery = window.matchMedia('(min-width: 320px) and (max-width: 430px)');
  private readonly toastSubject = new BehaviorSubject<NotificationMobileToastPayload | null>(null);
  private dismissTimer: ReturnType<typeof setTimeout> | null = null;
  /** Only the most recently received notification is shown in the banner. */
  private lastShownTimestamp = 0;
  private readonly domFallbackId = 'mobile-top-banner-fallback';
  private domRemoveTimer: ReturnType<typeof setTimeout> | null = null;
  readonly toast$ = this.toastSubject.asObservable();

  constructor(
    private ngZone: NgZone,
    private injector: Injector
  ) {
    console.warn(`${LOG_PREFIX} hello i comming — service constructor`);
    this.registerDebugHelpers();
  }

  isMobileViewport(): boolean {
    const width = window.innerWidth;
    // DevTools device mode sometimes desyncs matchMedia — innerWidth is the fallback.
    return (width >= 320 && width <= 430) || this.mobileMediaQuery.matches;
  }

  logViewportStatus(context: string): void {
    const width = window.innerWidth;
    const isMobile = this.isMobileViewport();
    console.warn(
      `${LOG_PREFIX} ${context} — viewport ${width}px, mobile banner ${isMobile ? 'enabled' : 'disabled (need 320–430px)'}`,
      { viewportWidth: width, isMobileViewport: isMobile, requiredRange: '320px – 430px' }
    );
  }

  /** Manual test from browser console: __testMobileTopBanner() */
  runDebugTest(): void {
    console.warn(`${LOG_PREFIX} hello i comming — manual test triggered`);
    if (!this.isMobileViewport()) {
      console.warn(`${LOG_PREFIX} viewport is NOT 320–430px — banner CSS is hidden. Set DevTools width to 375px, then run again.`, {
        viewportWidth: window.innerWidth
      });
    }
    this.show({
      title: 'Shippment',
      message: 'product description',
      createdAt: new Date().toISOString(),
      imageUrl: 'assets/images/default.jpg',
      rawNotification: {
        message: {
          title: 'Test notification',
          message: 'hello i comming',
          read: false,
          date: new Date().toISOString(),
          notification_type: 'test',
          id: 0
        }
      }
    }, { force: true });
  }

  private resolveVisibleMs(): number {
    return NOTIFICATION_MOBILE_TOAST_VISIBLE_MS;
  }

  private registerDebugHelpers(): void {
    const win = window as Window & {
      __testMobileTopBanner?: () => void;
      __mobileTopBannerStatus?: () => void;
      __mobileTopBannerDismiss?: () => void;
    };
    win.__testMobileTopBanner = () => this.runDebugTest();
    win.__mobileTopBannerStatus = () => this.logViewportStatus('manual status check');
    win.__mobileTopBannerDismiss = () => this.dismiss();
    console.warn(`${LOG_PREFIX} debug helpers — __testMobileTopBanner() (visible 3s), __mobileTopBannerDismiss()`);
  }

  showFromNotification(notification: Notification | Record<string, unknown>): void {
    if (!this.isMobileViewport()) {
      this.logViewportStatus('skipped showFromNotification — not mobile viewport');
      return;
    }

    const payload = this.toPayload(notification);
    if (!payload) {
      console.warn(`${LOG_PREFIX} skipped showFromNotification — empty title/message`, notification);
      return;
    }

    this.show(payload);
  }

  show(payload: NotificationMobileToastPayload, options?: { force?: boolean }): void {
    const incomingTimestamp = this.resolveTimestamp(payload.createdAt);
    if (!options?.force && incomingTimestamp < this.lastShownTimestamp) {
      console.warn(`${LOG_PREFIX} skipped older notification`, {
        title: payload.title,
        incomingTimestamp,
        lastShownTimestamp: this.lastShownTimestamp
      });
      return;
    }

    const visibleMs = this.resolveVisibleMs();
    this.lastShownTimestamp = incomingTimestamp;
    console.warn(`${LOG_PREFIX} hello i comming — showing latest banner`, {
      title: payload.title,
      message: payload.message,
      createdAt: payload.createdAt,
      visibleMs,
      visibleFor: '3s',
      isMobileViewport: this.isMobileViewport(),
      viewportWidth: window.innerWidth
    });

    this.ngZone.run(() => {
      this.clearDismissTimer();
      this.toastSubject.next(payload);
      this.dismissTimer = setTimeout(
        () => this.ngZone.run(() => this.dismiss()),
        visibleMs
      );
    });

    // Mobile banner is painted on document.body (isolated from layout/CSS clipping).
    if (this.shouldRenderMobileBanner(options)) {
      this.renderDomFallback(payload);
    }
  }

  dismiss(): void {
    console.warn(`${LOG_PREFIX} banner dismissed`);
    this.clearDismissTimer();
    this.clearDomFallback();
    this.toastSubject.next(null);
  }

  private shouldRenderMobileBanner(options?: { force?: boolean }): boolean {
    if (options?.force) {
      return true;
    }
    if (this.isMobileViewport()) {
      return true;
    }
    // Dev-only: allow manual console tests on desktop.
    return !environment.production;
  }

  /** Renders the mobile top banner on document.body (320–430px production target). */
  private renderDomFallback(payload: NotificationMobileToastPayload): void {
    if (typeof document === 'undefined') {
      return;
    }

    this.removeDomFallbackImmediate();

    const topPx = this.resolveBannerTopPx();
    const host = document.createElement('div');
    host.id = this.domFallbackId;
    host.className = 'mobile-top-banner-host';
    host.setAttribute('data-mobile-top-banner', 'true');
    host.style.top = `${topPx}px`;

    const card = document.createElement('button');
    card.type = 'button';
    card.className = 'mobile-top-banner-card';

    const avatar = this.createAvatarElement(payload);
    const body = document.createElement('div');
    body.className = 'mobile-top-banner-body';

    const title = document.createElement('div');
    title.className = 'mobile-top-banner-title';
    title.textContent = payload.title;

    const message = document.createElement('div');
    message.className = 'mobile-top-banner-message';
    message.textContent = payload.message;

    const time = document.createElement('span');
    time.className = 'mobile-top-banner-time';
    time.textContent = this.formatDisplayTime(payload.createdAt);

    body.append(title, message, time);
    card.append(avatar, body);
    card.addEventListener('click', () => this.ngZone.run(() => this.onBannerTap(payload)));
    host.append(card);

    host.setAttribute('aria-live', 'polite');
    (document.body ?? document.documentElement).appendChild(host);
    this.revealBannerHost(host, topPx);
  }

  /** Slide-in animation with a timeout fallback so the card never stays invisible. */
  private revealBannerHost(host: HTMLElement, topPx: number): void {
    void host.offsetHeight;
    host.classList.add('mobile-top-banner-host--visible');

    requestAnimationFrame(() => {
      host.classList.add('mobile-top-banner-host--visible');
    });

    window.setTimeout(() => {
      host.classList.add('mobile-top-banner-host--visible');
      const rect = host.getBoundingClientRect();
      console.warn(`${LOG_PREFIX} mobile banner rendered on screen`, {
        topPx,
        elementInDom: !!document.getElementById(this.domFallbackId),
        viewportWidth: window.innerWidth,
        rect: { top: rect.top, height: rect.height, opacity: getComputedStyle(host).opacity }
      });
    }, 80);
  }

  private onBannerTap(payload: NotificationMobileToastPayload): void {
    void import('src/app/Theme/layout/admin/notification.service').then(({ NotificationService }) => {
      this.ngZone.run(() => {
        try {
          this.injector.get(NotificationService).handleMobileToastTap(payload.rawNotification);
        } catch (error) {
          console.warn(`${LOG_PREFIX} banner tap — could not route notification`, error);
        }
        this.dismiss();
      });
    });
  }

  private clearDomFallback(): void {
    const host = document.getElementById(this.domFallbackId);
    if (!host) {
      return;
    }
    if (this.domRemoveTimer != null) {
      clearTimeout(this.domRemoveTimer);
      this.domRemoveTimer = null;
    }
    host.classList.remove('mobile-top-banner-host--visible');
    this.domRemoveTimer = setTimeout(() => {
      host.remove();
      this.domRemoveTimer = null;
    }, MOBILE_BANNER_SLIDE_OUT_MS);
  }

  private removeDomFallbackImmediate(): void {
    if (this.domRemoveTimer != null) {
      clearTimeout(this.domRemoveTimer);
      this.domRemoveTimer = null;
    }
    document.getElementById(this.domFallbackId)?.remove();
  }

  private toPayload(raw: Notification | Record<string, unknown>): NotificationMobileToastPayload | null {
    const record = raw as Record<string, unknown>;
    const nested = record['message'];

    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      const msg = nested as Record<string, unknown>;
      const title = String(msg['title'] ?? record['title'] ?? '').trim();
      const body = typeof msg['message'] === 'string'
        ? msg['message']
        : String(msg['description'] ?? record['description'] ?? '').trim();

      if (!title && !body) {
        return null;
      }

      return {
        title,
        message: body,
        createdAt: String(msg['date'] ?? msg['created_at'] ?? record['created_at'] ?? record['date'] ?? ''),
        imageUrl: (msg['image_url'] ?? msg['avatar'] ?? record['image_url'] ?? record['avatar'] ?? null) as string | null,
        rawNotification: raw as Notification
      };
    }

    const title = String(record['title'] ?? '').trim();
    const body = typeof record['message'] === 'string'
      ? record['message']
      : String(record['description'] ?? record['body'] ?? '').trim();

    if (!title && !body) {
      return null;
    }

    return {
      title,
      message: body,
      createdAt: String(record['date'] ?? record['created_at'] ?? ''),
      imageUrl: (record['image_url'] ?? record['avatar'] ?? null) as string | null,
      rawNotification: raw as Notification
    };
  }

  private createAvatarElement(payload: NotificationMobileToastPayload): HTMLElement {
    const avatar = document.createElement('span');
    avatar.className = 'mobile-top-banner-avatar';

    const initials = document.createElement('span');
    initials.className = 'mobile-top-banner-avatar-initials';
    initials.textContent = this.getInitials(payload.title);
    avatar.appendChild(initials);

    const imageUrl = payload.imageUrl?.trim();
    if (imageUrl) {
      const img = document.createElement('img');
      img.className = 'mobile-top-banner-avatar-image';
      img.src = imageUrl;
      img.alt = payload.title || 'Notification';
      img.addEventListener('error', () => {
        img.remove();
        initials.style.display = 'flex';
      });
      initials.style.display = 'none';
      avatar.insertBefore(img, initials);
    }

    return avatar;
  }

  private getInitials(title: string): string {
    if (!title?.trim()) {
      return 'N';
    }
    const words = title.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return words[0].slice(0, 2).toUpperCase();
  }

  /** Figma: "Just now" / "Last Wednesday at 9:42 AM" */
  private formatDisplayTime(date?: string): string {
    if (!date) {
      return '';
    }
    try {
      const notificationDate = new Date(date);
      if (Number.isNaN(notificationDate.getTime())) {
        return '';
      }
      const diffInMs = Date.now() - notificationDate.getTime();
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

      if (diffInMinutes < 1) {
        return 'Just now';
      }

      const timeStr = notificationDate.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
      const now = new Date();
      const diffInDays = Math.floor((now.getTime() - notificationDate.getTime()) / (1000 * 60 * 60 * 24));

      if (diffInDays === 0) {
        return `Today at ${timeStr}`;
      }
      if (diffInDays === 1) {
        return `Yesterday at ${timeStr}`;
      }
      if (diffInDays < 7) {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return `Last ${days[notificationDate.getDay()]} at ${timeStr}`;
      }
      return `${notificationDate.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} at ${timeStr}`;
    } catch {
      return '';
    }
  }

  /** Pixel offset from viewport top — uses live header height when available. */
  private resolveBannerTopPx(): number {
    if (typeof document === 'undefined') {
      return 60;
    }
    const header =
      (document.querySelector('app-nav-bar.pcoded-header') as HTMLElement | null)
      ?? (document.querySelector('app-nav-bar header') as HTMLElement | null)
      ?? (document.querySelector('app-nav-bar') as HTMLElement | null);
    const headerBottom = header?.getBoundingClientRect().bottom;
    if (headerBottom != null && headerBottom > 0) {
      return Math.round(headerBottom + 4);
    }
    const cssHeight = getComputedStyle(document.documentElement)
      .getPropertyValue('--header-toolbar-height-mobile')
      .trim();
    const parsed = cssHeight.endsWith('rem')
      ? parseFloat(cssHeight) * parseFloat(getComputedStyle(document.documentElement).fontSize || '16')
      : parseFloat(cssHeight);
    return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed + 4) : 60;
  }

  private resolveTimestamp(createdAt?: string): number {
    if (!createdAt) {
      return Date.now();
    }
    const parsed = new Date(createdAt).getTime();
    return Number.isNaN(parsed) ? Date.now() : parsed;
  }

  private clearDismissTimer(): void {
    if (this.dismissTimer != null) {
      clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }
}
